import { lazy, Suspense, useEffect, useRef, useCallback, useState } from "react";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import ProcessSection from "@/components/ProcessSection";
import ResultsBanner from "@/components/ResultsBanner";
import FitSection from "@/components/FitSection";
import QuotesStrip from "@/components/QuotesStrip";
import AboutMarketingSection from "@/components/AboutMarketingSection";
import AIStackPills from "@/components/AIStackPills";
import AISystemsSection from "@/components/AISystemsSection";
import FAQSection from "@/components/FAQSection";
import FooterCTA from "@/components/FooterCTA";
import BackToTop from "@/components/BackToTop";
import AdvisoryTeaserSection from "@/components/AdvisoryTeaserSection";
import ContactForm from "@/components/ContactForm";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation, useLang } from "@/lib/i18n";
import { LINKEDIN_URL } from "@/lib/constants";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { mountCalInline } from "@/lib/cal-bootstrap";
import { usePostHog } from "@posthog/react";

const NeuralNetwork3D = lazy(() => import("@/components/NeuralNetwork3D"));

const AITeams = () => {
  const t = useTranslation();
  const { lang } = useLang();
  const prefersReduced = useReducedMotion();
  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const bookCtaRef = useRef<HTMLAnchorElement>(null);
  const bookCtaContainerRef = useRef<HTMLDivElement>(null);
  const posthog = usePostHog();

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const origin = typeof e.origin === "string" ? e.origin : "";
      if (!origin.endsWith("cal.com")) return;
      const data = e.data;
      if (typeof data !== "object" || !data) return;
      const action = (data as { type?: string; action?: string }).action
        ?? (data as { type?: string }).type;
      if (typeof action === "string" && /booking.*success/i.test(action)) {
        posthog?.capture("discovery_call_booked", { page: "ai-teams", action });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [posthog]);

  useEffect(() => {
    const target = document.getElementById("cal-ai-teams");
    if (!target) return;
    let cleanup: (() => void) | null = null;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      cleanup = mountCalInline({
        elementOrSelector: "#cal-ai-teams",
        calLink: "chris-scibiorek/30min",
      });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          start();
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(target);
    const safety = setTimeout(start, window.innerWidth < 1024 ? 6000 : 3000);
    return () => {
      observer.disconnect();
      clearTimeout(safety);
      cleanup?.();
    };
  }, []);

  const canonical = lang === "pl" ? "https://chrisworkflows.com/pl/ai-teams" : "https://chrisworkflows.com/ai-teams";
  useDocumentMeta({
    title: t.aiTeams.meta.title,
    description: t.aiTeams.meta.description,
    canonical,
    robots: "index, follow",
    ogTitle: t.aiTeams.meta.title,
    ogDescription: t.aiTeams.meta.description,
    ogImage: `https://chrisworkflows.com/og/ai-teams-${lang}.jpg?v=3`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI for Marketing Teams",
      "serviceType": "AI Implementation Consulting",
      "description": t.aiTeams.meta.description,
      "url": canonical,
      "provider": { "@id": "https://chrisworkflows.com/#chris" },
      "areaServed": { "@type": "Place", "name": "Worldwide" },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Marketing teams, content and growth teams, startups and scale-ups",
      },
    },
  });

  const handleBookCtaMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !isFinePointer || !bookCtaRef.current || !bookCtaContainerRef.current) return;
    const r = bookCtaContainerRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) * 0.08;
    const dy = (e.clientY - cy) * 0.08;
    const max = 8;
    bookCtaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
  }, [prefersReduced, isFinePointer]);

  const handleBookCtaLeave = useCallback(() => {
    if (bookCtaRef.current) bookCtaRef.current.style.transform = "translate(0,0)";
  }, []);

  const b = t.aiTeams.booking;

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-x-hidden">
      {!isMobile && (
        <Suspense fallback={<div className="fixed inset-0 z-0 bg-background" />}>
          <NeuralNetwork3D />
        </Suspense>
      )}
      <div className="relative z-10">
        <Navbar />
        <HeroSection />
        <div className="container mx-auto px-6">
          <div className="w-16 h-px mx-auto bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        </div>
        <ServicesSection />
        <ProcessSection />
        <ResultsBanner />
        <FitSection />
        <QuotesStrip />
        <AboutMarketingSection />
        <AIStackPills
          items={t.aiTeams.stack.items}
          eyebrow={t.aiTeams.stack.eyebrow}
          heading={t.aiTeams.stack.heading}
          showLabel
        />
        <div className="container mx-auto px-6">
          <div className="w-16 h-px mx-auto bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        </div>
        <AdvisoryTeaserSection />
        <AISystemsSection />

        {/* Booking Section */}
        <section className="py-16 bg-card/20 border-y border-border/40">
          <div className="container mx-auto px-6">
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={anim.transition}
              className="mb-12 max-w-xl mx-auto text-center"
            >
              <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
                {b.eyebrow}
              </p>
              <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-4">{b.heading}</h2>
              <p className="text-sm font-body text-muted-foreground">{b.discoveryNote}</p>
            </motion.div>

            {/* Trust pills */}
            <div className="flex flex-wrap justify-center gap-4 mb-6 max-w-3xl mx-auto">
              {(lang === "pl" ? ["● 30 min", "● Darmowa pierwsza rozmowa", "● Zero presji"] : ["● 30 min", "● Free first call", "● Zero pressure"]).map((pill) => (
                <span key={pill} className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {pill}
                </span>
              ))}
            </div>

          </div>

          {/* Cal.com embed — frameless. Same asset as Advisory. */}
          <div id="book" className="mx-auto w-full max-w-[880px] px-4 sm:px-6 scroll-mt-20 lg:scroll-mt-[280px]">
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.15, ...anim.transition }}
              className="rounded-2xl overflow-hidden"
              ref={bookCtaContainerRef}
              onMouseMove={handleBookCtaMove}
              onMouseLeave={handleBookCtaLeave}
            >
              <div
                id="cal-ai-teams"
                className="h-[930px] sm:h-auto"
              />
            </motion.div>
          </div>

          <div className="container mx-auto px-6">
            <motion.p
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.2, ...anim.transition }}
              className="text-sm font-body text-muted-foreground mt-2 max-w-3xl mx-auto text-center"
            >
              {b.fallback}{" "}
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {b.fallbackLink}
              </a>
            </motion.p>

            <div className="mt-10">
              <ContactForm />
            </div>
          </div>
        </section>

        <FAQSection
          panelEyebrow={t.aiTeams.faq.panelEyebrow}
          panelHeading={t.aiTeams.faq.panelHeading}
          panelBody={t.aiTeams.faq.panelBody}
          panelCta={t.aiTeams.faq.panelCta}
        />
        <FooterCTA />
      </div>
      <BackToTop />
    </div>
  );
};

export default AITeams;
